import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefCallback, type RefObject } from 'react';
import {
  AddOutlined,
  ArrowUpwardOutlined,
  BlockOutlined,
  ChatBubbleOutlineRounded,
  CheckCircleOutlined,
  CloseOutlined,
  EditOutlined,
  ErrorOutlineOutlined,
  ExpandLessRounded,
  ExpandMoreRounded,
  HistoryRounded,
  RefreshRounded,
} from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Divider, IconButton, Stack, Tooltip, Typography, useMediaQuery, type Theme } from '@mui/material';
import type { WorkflowActivity, WorkflowActivityChange, WorkflowRequest } from './model';
import type { WorkflowActivityPort } from './port';
import { FormTextField, LoadingIndicator, SegmentedControl, ValueBadge, designTokens } from '../../design-system';
import { chronologicalWorkflowActivities, filterWorkflowActivities, formatWorkflowActivityValue, formatWorkflowActivityDay, formatWorkflowActivityTime, type WorkflowActivityFilter, type WorkflowActivityValueContext } from './models/workflowActivityPresentation';
import { formatTimestampDateKey } from '../../shared/formatTimestampDate';
import { isAmbiguousApiMutationFailure } from './workflowPageSupport';
import { latestWorkflowRevision, loadWorkflowActivityPages } from './models/workflowActivityLoading';
export type { WorkflowActivityValueContext } from './models/workflowActivityPresentation';

export type WorkflowCommentAttempt = { body: string; key: string };
export type WorkflowActivityComposer = {
  value: string;
  onChange: (value: string) => void;
  canEdit: boolean;
  disabled?: boolean;
  actions?: ReactNode;
  notice?: ReactNode;
  helperText?: string;
  error?: string;
  inputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  recovery?: { message: string; onDiscard: () => void };
  draftKey: string;
  commentAttempt?: WorkflowCommentAttempt;
  onCommentAttempt: (attempt: WorkflowCommentAttempt | undefined) => void;
};

type WorkflowActivityTimelineProps = {
  port: WorkflowActivityPort;
  request?: Pick<WorkflowRequest, 'id' | 'status'>;
  fixtureMode: boolean;
  canComment: boolean;
  visible?: boolean;
  refreshKey?: number;
  emptyMessage?: string;
  valueContext?: WorkflowActivityValueContext;
  onCommentDraftChange?: (dirty: boolean) => void;
  onCommentBusyChange?: (busy: boolean) => void;
  onApplied?: (requestId: string) => void;
  composer?: WorkflowActivityComposer;
  appearance?: 'plain' | 'panel';
  feedRef?: RefObject<HTMLDivElement | null>;
  revisionRef?: RefObject<HTMLLIElement | null>;
  revealRevisionKey?: number;
  inputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
};

type ActivityDayGroup = { key: string; label: string; activities: WorkflowActivity[] };
type ScrollIntent =
  | { type: 'bottom'; smooth: boolean; revealOnMobile?: boolean }
  | { type: 'revision'; revealOnMobile?: boolean }
  | { type: 'position'; previousTop: number }
  | { type: 'preserve'; previousHeight: number; previousTop: number };

const activityPageSize = 50;

const fixtureActivities: WorkflowActivity[] = [
  { id: 'fixture-created', kind: 'LIFECYCLE', action: 'CREATED', actor: { type: 'PERSON', id: 'person-me', displayName: '김소카' }, occurredAt: '2026-08-25T09:00:00Z', changes: [] },
  { id: 'fixture-changed', kind: 'CHANGE', action: 'CONTENT_CHANGED', actor: { type: 'PERSON', id: 'person-me', displayName: '김소카' }, occurredAt: '2026-08-25T09:05:00Z', changes: [
    { path: 'values.amount', fieldKey: 'amount', label: '금액', changeType: 'UPDATED', before: 1_000_000, after: 1_200_000 },
    { path: 'values.purpose', fieldKey: 'purpose', label: '사용 목적', changeType: 'UPDATED', before: '팀 행사', after: '팀 워크숍' },
  ] },
  { id: 'fixture-submitted', kind: 'LIFECYCLE', action: 'SUBMITTED', actor: { type: 'PERSON', id: 'person-me', displayName: '김소카' }, occurredAt: '2026-08-25T09:10:00Z', changes: [] },
  { id: 'fixture-comment', kind: 'COMMENT', action: 'COMMENTED', actor: { type: 'PERSON', id: 'person-03', displayName: '이민지' }, occurredAt: '2026-08-25T09:30:00Z', comment: '증빙 기준을 확인해 주세요.', changes: [] },
  { id: 'fixture-approved', kind: 'DECISION', action: 'APPROVED', actor: { type: 'PERSON', id: 'person-02', displayName: '우디(박진우)' }, occurredAt: '2026-08-26T01:20:00Z', step: { order: 1, label: '1차 조직장 승인', approvalLabel: '합의' }, comment: '예산 범위 확인했습니다.', changes: [] },
];

export function WorkflowActivityTimeline({ port, request, fixtureMode, canComment, visible = true, refreshKey = 0, emptyMessage = '아직 기록된 활동이 없습니다.', valueContext, onCommentDraftChange, onCommentBusyChange, onApplied, composer, appearance = 'plain', feedRef, revisionRef, revealRevisionKey = 0, inputRef }: WorkflowActivityTimelineProps) {
  const onAppliedRef = useRef(onApplied);
  onAppliedRef.current = onApplied;
  const [activities, setActivities] = useState<WorkflowActivity[]>([]);
  const [filter, setFilter] = useState<WorkflowActivityFilter>('all');
  const [nextCursor, setNextCursor] = useState('');
  const [loading, setLoading] = useState(Boolean(request));
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [newActivityCount, setNewActivityCount] = useState(0);
  const [error, setError] = useState('');
  const [errorSource, setErrorSource] = useState<'latest' | 'older'>('latest');
  const [reloadKey, setReloadKey] = useState(0);
  const [localBody, setBody] = useState('');
  const body = composer?.value ?? localBody;
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const idempotencyKey = useRef('');
  const postingRef = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { if (!composer) onCommentDraftChange?.(Boolean(body.trim())); }, [body, composer, onCommentDraftChange]);
  useEffect(() => { onCommentBusyChange?.(posting); }, [posting, onCommentBusyChange]);
  useEffect(() => () => { onCommentDraftChange?.(false); onCommentBusyChange?.(false); }, [onCommentDraftChange, onCommentBusyChange]);
  const requestID = request?.id ?? '';
  const composerKey = `${requestID}:${composer?.draftKey ?? ''}`;
  const currentComposerKey = useRef(composerKey);
  currentComposerKey.current = composerKey;
  const fixtureActivityStore = useRef({ requestID, activities: fixtureActivities });
  const scrollContainer = useRef<HTMLDivElement>(null);
  const localRevisionRef = useRef<HTMLLIElement>(null);
  const lastRevealRevisionKey = useRef(revealRevisionKey);
  const localInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const loadedRequestID = useRef('');
  const synchronizedIDs = useRef(new Set<string>());
  const activitiesRef = useRef(activities);
  activitiesRef.current = activities;
  const cursorRef = useRef(nextCursor);
  cursorRef.current = nextCursor;
  const scrollIntent = useRef<ScrollIntent | undefined>(undefined);
  const feedID = useId();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const desktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const queryKey = `${requestID}:${refreshKey}:${reloadKey}`;
  const currentQueryKey = useRef(queryKey);
  currentQueryKey.current = queryKey;
  if (fixtureActivityStore.current.requestID !== requestID) {
    fixtureActivityStore.current = { requestID, activities: fixtureActivities };
  }
  const needsRevision = request?.status === 'NEEDS_CHANGES';
  if (fixtureMode && needsRevision && !latestWorkflowRevision(fixtureActivityStore.current.activities)) {
    fixtureActivityStore.current.activities = appendActivity(fixtureActivityStore.current.activities, {
      id: `${requestID}-revision-feedback`, kind: 'DECISION', action: 'CHANGES_REQUESTED',
      actor: { type: 'PERSON', id: 'person-02', displayName: '우디(박진우)' },
      occurredAt: '2026-08-26T01:25:00Z',
      comment: '신청 내용과 증빙의 금액을 확인하고 사용 목적을 구체적으로 작성해 주세요.', changes: [],
    });
  }

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const initial = loadedRequestID.current !== requestID || activitiesRef.current.length === 0;
    loadedRequestID.current = requestID;
    if (initial) synchronizedIDs.current = new Set();
    const knownIDs = new Set(synchronizedIDs.current);
    const previousCursor = cursorRef.current;
    setLoading(initial && Boolean(requestID));
    setRefreshing(!initial && Boolean(requestID));
    setLoadingOlder(false);
    setError('');
    setErrorSource('latest');
    if (initial) {
      setActivities([]); setNextCursor(''); setNewActivityCount(0);
      scrollIntent.current = needsRevision ? { type: 'revision' } : { type: 'bottom', smooth: false };
    }
    if (!requestID) return () => { active = false; };
    const source = async () => {
      const page = fixtureMode
        ? { data: chronologicalWorkflowActivities(fixtureActivityStore.current.activities), nextCursor: undefined as string | undefined }
        : await loadWorkflowActivityPages(port, requestID, { knownIDs, includeLatestRevision: initial && needsRevision, signal: controller.signal });
      return { data: page.data, nextCursor: initial || !knownIDs.size ? page.nextCursor : previousCursor };
    };
    void source().then((page) => {
      if (!active) return;
      page.data.forEach(activity => synchronizedIDs.current.add(activity.id));
      if (!initial) {
        scrollIntent.current = { type: 'position', previousTop: scrollContainer.current?.scrollTop ?? 0 };
        const displayedIDs = new Set(activitiesRef.current.map(activity => activity.id));
        const added = new Set(page.data.filter(activity => !displayedIDs.has(activity.id)).map(activity => activity.id)).size;
        setNewActivityCount(current => current + added);
        setAnnouncement(added ? `새 활동 ${added}개를 불러왔습니다.` : '최신 활동을 확인했습니다.');
      }
      setActivities(current => chronologicalWorkflowActivities(prependActivities(current, page.data)));
      setNextCursor(page.nextCursor ?? '');
      if (page.data.some(activity => activity.action === 'APPLIED')) onAppliedRef.current?.(requestID);
    }).catch((reason: unknown) => {
      if (active) setError(reason instanceof Error ? reason.message : '활동 이력을 불러오지 못했습니다.');
    }).finally(() => {
      if (active) { setLoading(false); setRefreshing(false); }
    });
    return () => { active = false; controller.abort(); };
  }, [port, fixtureMode, queryKey, requestID, needsRevision]);

  useEffect(() => {
    const refresh = () => {
      if (visible && requestID && document.visibilityState === 'visible' && !loading && !refreshing && !loadingOlder && !postingRef.current) setReloadKey(value => value + 1);
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [visible, requestID, loading, refreshing, loadingOlder]);

  useEffect(() => {
    setBody('');
    setPosting(false);
    postingRef.current = false;
    setPostError('');
    setAnnouncement('');
    setFilter('all');
    idempotencyKey.current = '';
  }, [composerKey]);

  useLayoutEffect(() => {
    if (lastRevealRevisionKey.current === revealRevisionKey) return;
    lastRevealRevisionKey.current = revealRevisionKey;
    setFilter('all');
    scrollIntent.current = { type: 'revision', revealOnMobile: true };
  }, [revealRevisionKey]);

  useLayoutEffect(() => {
    if (!visible || loading || !scrollIntent.current || !scrollContainer.current) return;
    const element = scrollContainer.current;
    const intent = scrollIntent.current;
    if (intent.type === 'revision' && filter !== 'all') return;
    scrollIntent.current = undefined;
    if (intent.type === 'revision') {
      const target = localRevisionRef.current;
      if (intent.revealOnMobile) (target ?? element).focus({ preventScroll: true });
      if (desktop) {
        element.scrollTo({ top: target ? element.scrollTop + target.getBoundingClientRect().top - element.getBoundingClientRect().top : element.scrollHeight, behavior: 'auto' });
      } else if (intent.revealOnMobile) {
        (target ?? element).scrollIntoView({ block: 'start', behavior: 'auto' });
      }
      return;
    }
    if (!desktop) {
      if (intent.type === 'bottom' && intent.revealOnMobile) element.scrollIntoView({ block: 'end', behavior: intent.smooth && !reducedMotion ? 'smooth' : 'auto' });
      return;
    }
    if (intent.type === 'position') { element.scrollTop = intent.previousTop; return; }
    if (intent.type === 'preserve') {
      element.scrollTop = intent.previousTop + element.scrollHeight - intent.previousHeight;
      return;
    }
    element.scrollTo({ top: element.scrollHeight, behavior: intent.smooth && !reducedMotion ? 'smooth' : 'auto' });
  }, [activities, loading, reducedMotion, desktop, visible, newActivityCount, filter, revealRevisionKey]);

  const visibleActivities = useMemo(() => filterWorkflowActivities(activities, filter), [activities, filter]);
  const activityGroups = useMemo(() => groupActivitiesByDay(visibleActivities), [visibleActivities]);
  const latestRevisionID = latestWorkflowRevision(activities)?.id;
  const bodyLength = Array.from(body).length;
  const recovering = Boolean(composer?.recovery);
  const commentAllowed = !recovering && canComment && (['IN_PROGRESS', 'NEEDS_CHANGES', 'COMPLETED'].includes(request?.status ?? '') || Boolean(composer?.commentAttempt));
  const editable = recovering || (composer ? composer.canEdit || commentAllowed : commentAllowed);

  const loadOlder = async () => {
    if (!requestID || !nextCursor || loadingOlder || refreshing || fixtureMode) return;
    const requestedQueryKey = queryKey;
    setLoadingOlder(true);
    setError('');
    try {
      const page = await port.listWorkflowRequestActivities(requestID, nextCursor, activityPageSize);
      if (currentQueryKey.current !== requestedQueryKey) return;
      page.data.forEach(activity => synchronizedIDs.current.add(activity.id));
      const element = scrollContainer.current;
      if (element) {
        scrollIntent.current = { type: 'preserve', previousHeight: element.scrollHeight, previousTop: element.scrollTop };
      }
      setActivities((current) => chronologicalWorkflowActivities(prependActivities(current, page.data)));
      setNextCursor(page.nextCursor ?? '');
    } catch (reason) {
      if (currentQueryKey.current === requestedQueryKey) {
        setErrorSource('older');
        setError(reason instanceof Error ? reason.message : '이전 활동을 불러오지 못했습니다.');
      }
    } finally {
      if (currentQueryKey.current === requestedQueryKey) setLoadingOlder(false);
    }
  };

  const createComment = async () => {
    const normalized = composer?.commentAttempt?.body ?? body.trim();
    if (!request || !normalized || postingRef.current || !commentAllowed || composer?.disabled || bodyLength > 2000) return;
    const commentComposerKey = composerKey;
    const active = () => mounted.current && currentComposerKey.current === commentComposerKey;
    postingRef.current = true;
    setPosting(true);
    setPostError('');
    setAnnouncement('');
    const attempt = composer?.commentAttempt ?? { body: normalized, key: idempotencyKey.current || crypto.randomUUID() };
    idempotencyKey.current = attempt.key;
    composer?.onCommentAttempt(attempt);
    try {
      const created: WorkflowActivity = fixtureMode
        ? { id: `fixture-comment-${Date.now()}`, kind: 'COMMENT', action: 'COMMENTED', actor: { type: 'PERSON', id: 'person-me', displayName: '김소카' }, occurredAt: new Date().toISOString(), comment: normalized, changes: [] }
        : await port.createWorkflowRequestComment(request.id, attempt.body, attempt.key);
      if (!active()) return;
      if (fixtureMode) fixtureActivityStore.current.activities = appendActivity(fixtureActivityStore.current.activities, created);
      if (composer) { composer.onCommentAttempt(undefined); composer.onChange(''); }
      else setBody('');
      idempotencyKey.current = '';
      scrollIntent.current = { type: 'bottom', smooth: true };
      setActivities((current) => appendActivity(current, created));
      if (filter === 'history') setFilter('comments');
      setAnnouncement('댓글이 등록되었습니다.');
      setReloadKey(value => value + 1);
    } catch (reason) {
      if (active()) {
        if (!isAmbiguousApiMutationFailure(reason)) { composer?.onCommentAttempt(undefined); idempotencyKey.current = ''; }
        setPostError(reason instanceof Error ? reason.message : '댓글을 등록하지 못했습니다.');
      }
    } finally {
      if (active()) { postingRef.current = false; setPosting(false); }
    }
  };

  const historyContent = <Box key="history"
          id={feedID}
          ref={(element: HTMLDivElement | null) => { scrollContainer.current = element; if (feedRef) feedRef.current = element; }}
          data-testid="workflow-activity-scroll"
          aria-busy={loading || loadingOlder || refreshing}
          aria-label={`${filter === 'comments' ? '댓글' : filter === 'history' ? '변경 이력' : '전체 활동 이력'}, 오래된 항목부터 최신 항목 순서`}
          tabIndex={desktop ? 0 : -1}
          sx={{ minHeight: 0, flex: { md: 1 }, overflowY: { xs: 'visible', md: 'auto' }, overscrollBehavior: { md: 'contain' }, overflowAnchor: 'none', scrollbarGutter: { md: 'stable' } }}
        >
          {!request ? <Stack spacing={0.75} sx={{ minHeight: 220, p: 2.5, alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'text.secondary' }}><HistoryRounded /><Typography variant="body2">{emptyMessage}</Typography></Stack> : null}
          {request && loading ? <Stack role="status" sx={{ minHeight: 180, p: 2, alignItems: 'center', justifyContent: 'center' }}><LoadingIndicator label="활동 이력을 불러오는 중입니다." /></Stack> : null}
          {!loading && nextCursor ? <Box sx={{ px: 1.5, pt: 1.25 }}><Button fullWidth variant="text" loading={loadingOlder} disabled={refreshing} onClick={() => void loadOlder()}>이전 기록 불러오기</Button></Box> : null}
          {error ? <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => errorSource === 'older' ? void loadOlder() : setReloadKey((value) => value + 1)}>다시 시도</Button>} sx={{ m: 1.5 }}>{error}</Alert> : null}
          {request && !loading && !error && activities.length === 0 ? <Stack spacing={0.75} sx={{ minHeight: 220, p: 2.5, alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'text.secondary' }}><HistoryRounded /><Typography variant="body2">{emptyMessage}</Typography></Stack> : null}
          {request && !loading && !error && activities.length > 0 && visibleActivities.length === 0 ? <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 3 }}>{filter === 'comments' ? '불러온 기록에 댓글이 없습니다.' : '불러온 기록에 변경 이력이 없습니다.'}{nextCursor ? ' 이전 기록을 불러와 확인할 수 있습니다.' : ''}</Typography> : null}
          {!loading && visibleActivities.length > 0 ? <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {activityGroups.map((group) => <Box component="li" key={group.key}>
              <Stack direction="row" spacing={1} sx={{ px: 1.75, pt: 1.75, pb: 0.5, alignItems: 'center' }}>
                <Typography component="h3" variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{group.label}</Typography>
              </Stack>
              <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {group.activities.map((activity, index) => <WorkflowActivityItem key={activity.id} activity={activity} first={index === 0} last={index === group.activities.length - 1} valueContext={valueContext}
                  itemRef={activity.id === latestRevisionID ? (element) => { localRevisionRef.current = element; if (revisionRef) revisionRef.current = element; } : undefined} />)}
              </Box>
            </Box>)}
          </Box> : null}
        </Box>;

  const composerContent = editable || composer?.notice || composer?.actions ? <Stack key="composer"
          data-testid="workflow-activity-composer"
          spacing={1}
          sx={{ minHeight: 0, maxHeight: { md: '50%' }, overflowY: { md: 'auto' }, overscrollBehavior: { md: 'contain' }, flex: '0 0 auto', px: 1.5, py: composer ? 1 : 1.5,
            borderTop: { xs: 0, md: `1px solid ${designTokens.color.divider}` },
            borderBottom: { xs: `1px solid ${designTokens.color.divider}`, md: 0 },
            bgcolor: appearance === 'panel' ? 'transparent' : designTokens.color.canvas }}
        >
          {composer?.notice}
          {composer?.recovery ? <Typography variant="body2" color="text.secondary">{composer.recovery.message}</Typography> : null}
          {composer?.commentAttempt && !posting ? <Alert severity="warning">댓글 등록 결과를 확인하지 못했습니다. 같은 의견으로 다시 등록해 결과를 확인해 주세요.</Alert> : null}
          {editable ? <Stack spacing={1}>
            <FormTextField
              label={composer ? '의견' : '댓글'}
              labelSuffix={bodyLength || !composer ? `${bodyLength}/2000` : undefined}
              value={body}
              inputRef={(element: HTMLInputElement | HTMLTextAreaElement | null) => { localInputRef.current = element; if (inputRef) inputRef.current = element; if (composer?.inputRef) composer.inputRef.current = element; }}
              disabled={posting || composer?.disabled || Boolean(composer?.commentAttempt)}
              error={Boolean(composer?.error)}
              helperText={composer?.error || composer?.helperText}
              onChange={(event) => { if (composer) composer.onChange(event.target.value); else setBody(event.target.value); idempotencyKey.current = ''; setPostError(''); }}
              onKeyDown={(event) => {
                if (commentAllowed && event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  void createComment();
                }
              }}
              multiline
              minRows={1}
              maxRows={composer ? 3 : 4}
              slotProps={{
                input: composer ? { readOnly: recovering, sx: { minHeight: designTokens.layout.controlMinHeight, [designTokens.media.touchTarget]: { minHeight: designTokens.layout.touchTargetMinSize } } } : undefined,
                htmlInput: { maxLength: 2000, ...(commentAllowed ? { 'aria-keyshortcuts': 'Control+Enter Meta+Enter' } : {}) },
              }}
              placeholder={composer ? '의견을 입력해 주세요.' : '댓글을 입력해 주세요.'}
              sx={{ minWidth: 0 }}
            />
            <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
            {composer?.recovery ? <>
              <Button size="small" onClick={async () => {
                try { await navigator.clipboard.writeText(body); setAnnouncement('의견을 복사했습니다.'); } catch {
                  localInputRef.current?.focus(); localInputRef.current?.select();
                  setPostError('자동 복사를 사용할 수 없습니다. 선택된 의견을 직접 복사해 주세요.');
                }
              }}>의견 복사</Button>
              <Button size="small" onClick={composer.recovery.onDiscard}>의견 폐기</Button>
            </> : null}
            {commentAllowed ? <Button
                size={composer ? 'small' : 'medium'}
                variant={composer ? 'outlined' : 'contained'}
                sx={composer?.actions ? { mr: 'auto' } : undefined}
                aria-label={composer?.commentAttempt && !posting ? '댓글 등록 다시 시도' : '댓글 등록'}
                title="Ctrl/⌘ + Enter로 등록"
                disabled={posting || composer?.disabled || body.trim().length === 0 || bodyLength > 2000}
                onClick={() => void createComment()}
                startIcon={posting ? <CircularProgress size={18} color="inherit" /> : composer ? undefined : <ChatBubbleOutlineRounded fontSize="small" />}
              >{composer?.commentAttempt && !posting ? '댓글 등록 다시 시도' : '댓글 등록'}</Button> : null}
            {composer?.actions ? <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>{composer.actions}</Stack> : null}
            </Stack>
          </Stack> : composer?.actions}
          {postError && !composer?.commentAttempt ? <Alert severity="error">{postError}</Alert> : null}
          {recovering && announcement ? <Typography role="status" variant="caption">{announcement}</Typography> : null}
        </Stack> : null;

  return <Box
    component="section"
    aria-labelledby={`${feedID}-heading`}
    data-testid="workflow-activity-rail"
    sx={{
      minHeight: 0,
      height: { md: '100%' },
      display: 'flex',
      flexDirection: 'column',
      ...(appearance === 'panel' ? {
        boxSizing: 'border-box',
        bgcolor: designTokens.color.canvas,
        border: `1px solid ${designTokens.color.divider}`,
        borderRadius: designTokens.radius.surface,
      } : {}),
    }}
  >
    <Stack direction="row" useFlexGap sx={{ gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', ...(appearance === 'panel' ? { px: 1.5, py: 1, borderBottom: `1px solid ${designTokens.color.divider}` } : {}) }}>
    <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
    <Typography
      id={`${feedID}-heading`}
      component="h2"
      variant="subtitle1"
      sx={{ ...designTokens.typography.componentTitle, flex: '0 0 auto' }}
    >{composer ? '활동 및 처리' : '댓글 및 변경 이력'}</Typography>
    <Tooltip title="활동 새로고침"><span><IconButton size="small" aria-label="활동 새로고침" loading={refreshing} disabled={!requestID || loading || loadingOlder || posting} onClick={() => setReloadKey(value => value + 1)}><RefreshRounded /></IconButton></span></Tooltip>
    </Stack>
    <SegmentedControl value={filter} onChange={setFilter} ariaLabel="활동 보기" controls={feedID} items={[{ value: 'all', label: '전체' }, { value: 'comments', label: '댓글' }, { value: 'history', label: '이력' }]} />
    </Stack>
    {newActivityCount > 0 ? <Button size="small" onClick={() => {
      setFilter('all'); setNewActivityCount(0);
      scrollIntent.current = { type: 'bottom', smooth: false, revealOnMobile: true };
    }}>새 활동 {newActivityCount}개 보기</Button> : null}
    <Box sx={{ mt: appearance === 'panel' ? 0 : 1, minHeight: 0, flex: { md: 1 }, display: 'flex', flexDirection: 'column', bgcolor: appearance === 'panel' ? 'transparent' : designTokens.color.canvas }}>
        {desktop ? [historyContent, composerContent] : [composerContent, historyContent]}
        {!recovering ? <Typography role="status" aria-live="polite" sx={visuallyHiddenSx}>{announcement}</Typography> : null}
    </Box>
  </Box>;
}

function WorkflowActivityItem({ activity, first, last, valueContext, itemRef }: { activity: WorkflowActivity; first: boolean; last: boolean; valueContext?: WorkflowActivityValueContext; itemRef?: RefCallback<HTMLLIElement> }) {
  const [expanded, setExpanded] = useState(false);
  const changesID = useId();
  const hasChanges = activity.changes.length > 0;
  const failedApplication = activity.action === 'APPLY_FAILED';
  const decisionComment = !failedApplication && activity.kind === 'DECISION' && activity.comment;
  const standaloneComment = activity.kind === 'COMMENT' && activity.comment;
  return <Box component="li" ref={itemRef} tabIndex={itemRef ? -1 : undefined} data-testid="workflow-activity-item" data-activity-id={activity.id} data-occurred-at={activity.occurredAt} sx={{ position: 'relative', px: 1.75, py: 1.25 }}>
    <Stack direction="row" spacing={1.25} sx={{ alignItems: 'stretch' }}>
      <Box aria-hidden sx={{ position: 'relative', width: 28, flex: '0 0 28px', display: 'flex', justifyContent: 'center' }}>
        {!first ? <Box sx={{ position: 'absolute', top: -10, bottom: '50%', width: '1px', bgcolor: designTokens.color.divider }} /> : null}
        {!last ? <Box sx={{ position: 'absolute', top: '50%', bottom: -10, width: '1px', bgcolor: designTokens.color.divider }} /> : null}
        <Box sx={{ mt: 0.25, width: 28, height: 28, zIndex: 1, display: 'grid', placeItems: 'center', borderRadius: designTokens.radius.circle, bgcolor: designTokens.color.canvas, color: activityColor(activity), border: `2px solid ${designTokens.color.canvas}` }}>{activityMarker(activity)}</Box>
      </Box>
      <Box component="article" aria-label={activitySentence(activity)} sx={{ minWidth: 0, flex: 1, pb: 0.25 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{activitySentence(activity)}</Typography>
        <Typography component="time" dateTime={activity.occurredAt} variant="caption" color="text.secondary">{formatWorkflowActivityTime(activity.occurredAt)}{failedApplication ? ` · ${activity.actor.displayName}` : ''}</Typography>
        {failedApplication ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{activity.comment || '변경 내용을 적용하지 못했습니다.'}</Typography> : null}
        {activity.action === 'APPLIED' ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>승인된 변경 내용이 적용되었습니다.</Typography> : null}
        {activity.step ? <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>{activity.step.order}단계 · {activity.step.label}{activity.step.approvalLabel ? <> · <ValueBadge size="compact" label={activity.step.approvalLabel} /></> : null}</Typography> : null}
        {standaloneComment ? <Box sx={{ mt: 1, px: 1.25, py: 1, borderRadius: designTokens.radius.surface, bgcolor: designTokens.color.subtleSurface }}><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{activity.comment}</Typography></Box> : null}
        {decisionComment ? <Box sx={{ mt: 1, px: 1.25, py: 1, borderLeft: `2px solid ${designTokens.color.divider}`, bgcolor: designTokens.color.subtleSurface }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.35, color: 'text.secondary', fontWeight: 600 }}>처리 의견</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{activity.comment}</Typography>
        </Box> : null}
        {activity.comment && !standaloneComment && !decisionComment && !failedApplication ? <Box sx={{ mt: 1, px: 1.25, py: 1, borderRadius: designTokens.radius.surface, bgcolor: designTokens.color.subtleSurface }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.35, color: 'text.secondary', fontWeight: 750 }}>{activity.action === 'CANCELLED' ? '취소 사유' : '의견'}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{activity.comment}</Typography>
        </Box> : null}
        {hasChanges ? <>
          <Button size="small" aria-expanded={expanded} aria-controls={changesID} endIcon={expanded ? <ExpandLessRounded /> : <ExpandMoreRounded />} onClick={() => setExpanded((value) => !value)} sx={{ mt: 0.5 }}>{expanded ? '변경 내용 접기' : `변경 내용 ${activity.changes.length}건`}</Button>
          {expanded ? <Stack id={changesID} spacing={1.5} sx={{ mt: 0.5 }}>{activity.changes.map((change, index) => <WorkflowActivityChangeRow key={`${change.path}-${change.fieldKey ?? ''}-${index}`} change={change} valueContext={valueContext} />)}</Stack> : null}
        </> : null}
      </Box>
    </Stack>
  </Box>;
}

function WorkflowActivityChangeRow({ change, valueContext }: { change: WorkflowActivityChange; valueContext?: WorkflowActivityValueContext }) {
  return <Box sx={{ pl: 1.25, py: 0.5, borderLeft: `1px solid ${designTokens.color.divider}` }}>
    <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}>{change.label} · <ValueBadge size="compact" label={changeTypeLabel(change.changeType)} /></Typography>
    <Stack spacing={0.75}>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>이전</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{formatWorkflowActivityValue(change, change.before, valueContext)}</Typography>
      </Box>
      <Divider />
      <Box>
        <Typography variant="caption" sx={{ display: 'block', mb: 0.25, color: activityColorFromChange(change), fontWeight: 750 }}>변경</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{formatWorkflowActivityValue(change, change.after, valueContext)}</Typography>
      </Box>
    </Stack>
  </Box>;
}

function prependActivities(current: WorkflowActivity[], older: WorkflowActivity[]) {
  const currentIDs = new Set(current.map((activity) => activity.id));
  return [...older.filter((activity) => !currentIDs.has(activity.id)), ...current];
}

function appendActivity(current: WorkflowActivity[], created: WorkflowActivity) {
  return chronologicalWorkflowActivities([...current.filter((activity) => activity.id !== created.id), created]);
}

function groupActivitiesByDay(activities: WorkflowActivity[]): ActivityDayGroup[] {
  const groups: ActivityDayGroup[] = [];
  for (const activity of activities) {
    const key = formatTimestampDateKey(activity.occurredAt) || 'unknown';
    const current = groups[groups.length - 1];
    if (current?.key === key) current.activities.push(activity);
    else groups.push({ key, label: formatWorkflowActivityDay(activity.occurredAt), activities: [activity] });
  }
  return groups;
}

function activitySentence(activity: WorkflowActivity) {
  if (activity.action === 'APPLIED') return '적용 완료';
  if (activity.action === 'APPLY_FAILED') return '적용 실패';
  const actor = activity.actor.type === 'SYSTEM' ? `${activity.actor.displayName}이` : `${activity.actor.displayName}님이`;
  const action = ({
    CREATED: '요청 초안을 만들었습니다.',
    CONTENT_CHANGED: activity.changes.length > 0 ? `요청 내용 ${activity.changes.length}건을 변경했습니다.` : '요청 내용을 변경했습니다.',
    SUBMITTED: '요청을 제출했습니다.',
    APPROVED: '요청을 승인했습니다.',
    REJECTED: '요청을 반려했습니다.',
    CHANGES_REQUESTED: '내용 보완을 요청했습니다.',
    CANCELLED: '요청을 취소했습니다.',
    COMMENTED: '댓글을 남겼습니다.',
  } as Record<WorkflowActivity['action'], string>)[activity.action];
  return `${actor} ${action}`;
}

function changeTypeLabel(value: WorkflowActivityChange['changeType']) {
  return ({ ADDED: '추가', UPDATED: '변경', REMOVED: '삭제', REORDERED: '순서 변경' } as const)[value];
}

function activityMarker(activity: WorkflowActivity) {
  const iconSx = { fontSize: 16 };
  switch (activity.action) {
    case 'CREATED': return <AddOutlined sx={iconSx} />;
    case 'CONTENT_CHANGED': return <EditOutlined sx={iconSx} />;
    case 'SUBMITTED': return <ArrowUpwardOutlined sx={iconSx} />;
    case 'APPROVED':
    case 'APPLIED': return <CheckCircleOutlined sx={iconSx} />;
    case 'CHANGES_REQUESTED': return <EditOutlined sx={iconSx} />;
    case 'APPLY_FAILED': return <ErrorOutlineOutlined sx={iconSx} />;
    case 'REJECTED': return <BlockOutlined sx={iconSx} />;
    case 'CANCELLED': return <CloseOutlined sx={iconSx} />;
    case 'COMMENTED': return <ChatBubbleOutlineRounded sx={iconSx} />;
  }
}

function activityColor(activity: WorkflowActivity) {
  if (activity.action === 'REJECTED' || activity.action === 'CANCELLED' || activity.action === 'APPLY_FAILED') return designTokens.color.danger;
  if (activity.action === 'CHANGES_REQUESTED') return designTokens.color.warningDark;
  if (activity.action === 'APPROVED' || activity.action === 'APPLIED') return designTokens.color.successDark;
  return designTokens.color.secondaryText;
}

function activityColorFromChange(change: WorkflowActivityChange) {
  return change.changeType === 'REMOVED' ? designTokens.color.danger : designTokens.color.action;
}

const visuallyHiddenSx = {
  position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden',
  clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
} as const;
